/**
 * Runtime configuration for the Aridel web app.
 *
 * Values come from Vite environment variables prefixed `VITE_`. They are
 * baked into the JS bundle at build time. Configure in the deploy
 * environment (e.g. GitHub Actions workflow env, .env.local for dev).
 *
 * Chat (including file attachments), settings, insights, and conversation
 * history all go through `aridel-client.ts` / `app-client.ts`.
 */

import { STATIC_CATALOG, OTHER_GROUP_LABEL } from './model-catalog';

// ---------------------------------------------------------------------------
// Required env vars (build will fail at first usage if any are missing).
// ---------------------------------------------------------------------------
function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `Missing required env var ${name}. Set it in the deploy environment ` +
        `(GitHub Actions workflow env, or .env.local for dev).`,
    );
  }
  return value;
}

export const AWS_REGION =
  (import.meta.env.VITE_AWS_REGION as string | undefined) ?? 'us-east-1';

export const USER_POOL_ID = required(
  'VITE_USER_POOL_ID',
  import.meta.env.VITE_USER_POOL_ID,
);
export const APP_CLIENT_ID = required(
  'VITE_APP_CLIENT_ID',
  import.meta.env.VITE_APP_CLIENT_ID,
);
export const COGNITO_DOMAIN = required(
  'VITE_COGNITO_DOMAIN',
  import.meta.env.VITE_COGNITO_DOMAIN,
);

export const PUBLIC_API_BASE_URL =
  (import.meta.env.VITE_PUBLIC_API_BASE_URL as string | undefined) ??
  'https://api.aridel.ai/v1';
export const APP_API_BASE_URL = required(
  'VITE_APP_API_BASE_URL',
  import.meta.env.VITE_APP_API_BASE_URL,
);
const _origin =
  typeof window !== 'undefined' ? `${window.location.origin}/` : '/';

export const REDIRECT_SIGN_IN =
  (import.meta.env.VITE_REDIRECT_SIGN_IN as string | undefined) ?? _origin;
export const REDIRECT_SIGN_OUT =
  (import.meta.env.VITE_REDIRECT_SIGN_OUT as string | undefined) ?? _origin;

// Cognito identifiers under their legacy names (some components still
// import these directly instead of going through `USER_POOL_ID` etc.).
export const VITE_AWS_REGION = AWS_REGION;
export const VITE_USER_POOL_ID = USER_POOL_ID;
export const VITE_APP_CLIENT_ID = APP_CLIENT_ID;
export const VITE_COGNITO_DOMAIN = COGNITO_DOMAIN;
export const VITE_REDIRECT_SIGN_IN = REDIRECT_SIGN_IN;
export const VITE_REDIRECT_SIGN_OUT = REDIRECT_SIGN_OUT;

// ---------------------------------------------------------------------------
// Application settings.
// ---------------------------------------------------------------------------
export const POLLING_INTERVAL_MS = parseInt(
  (import.meta.env.VITE_POLLING_INTERVAL_MS as string | undefined) ?? '3000',
  10,
);
export const MAX_POLLING_ATTEMPTS = parseInt(
  (import.meta.env.VITE_MAX_POLLING_ATTEMPTS as string | undefined) ?? '20',
  10,
);
export const DEMO_PROMPT_LIMIT = parseInt(
  (import.meta.env.VITE_DEMO_PROMPT_LIMIT as string | undefined) ?? '3',
  10,
);

// ---------------------------------------------------------------------------
// File attachment limits — mirror the public API (shared/attachments.py):
// 4 MB raw per file, 7 MB total per request, 5 attachments per message.
// ---------------------------------------------------------------------------
export const MAX_FILE_SIZE_MB = parseInt(
  (import.meta.env.VITE_MAX_FILE_SIZE_MB as string | undefined) ?? '4',
  10,
);
export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
export const MAX_FILES_PER_MESSAGE = 5;

// ---------------------------------------------------------------------------
// Model catalog — these are the BUILD-TIME (static) values, derived from the
// bundled models.json for instant first paint. At runtime the model selectors
// use ModelCatalogContext, which fetches the live catalog from GET /v1/models
// and falls back to these. Derivation logic lives in ./model-catalog.
// ---------------------------------------------------------------------------
export const PROPRIETARY_MODELS = STATIC_CATALOG.proprietaryModels;
export const OPEN_SOURCE_MODELS = STATIC_CATALOG.openSourceModels;
export const ALL_MODELS = STATIC_CATALOG.allModels;
export const PROVIDER_GROUPS = STATIC_CATALOG.providerGroups;
export const MODEL_PRICING = STATIC_CATALOG.modelPricing;
export const MODEL_TIER = STATIC_CATALOG.modelTier;
export { OTHER_GROUP_LABEL };

// ---------------------------------------------------------------------------
// New TS clients use this key to cache the user's `aridel_live_*` API key
// in `sessionStorage` (populated at sign-in by `app-client.ts`).
// ---------------------------------------------------------------------------
export const API_KEY_STORAGE_KEY = 'aridel_api_key';
